import { useEffect, useState } from "react";
import { History, Trash2, Truck, DollarSign, CheckCircle2, Edit2, Users, HandCoins, Search } from "lucide-react";
import api from "@/lib/api";

const ACTION_META = {
  vehicle_deleted: { icon: Trash2, cls: "text-primary", key: "act_vehicle_deleted" },
  vehicle_transferred: { icon: Truck, cls: "text-cyan-400", key: "act_vehicle_transferred" },
  vehicle_funded: { icon: CheckCircle2, cls: "text-success", key: "act_vehicle_funded" },
  vehicle_unfunded: { icon: CheckCircle2, cls: "text-warning", key: "act_vehicle_unfunded" },
  sale_corrected: { icon: Edit2, cls: "text-warning", key: "act_sale_corrected" },
  expense_created: { icon: DollarSign, cls: "text-text-secondary", key: "act_expense_created" },
  expense_deleted: { icon: DollarSign, cls: "text-primary", key: "act_expense_deleted" },
  receivable_deleted: { icon: HandCoins, cls: "text-primary", key: "act_receivable_deleted" },
  team_member_deleted: { icon: Users, cls: "text-primary", key: "act_team_member_deleted" },
  salesperson_transferred: { icon: Users, cls: "text-cyan-400", key: "act_salesperson_transferred" },
};

function relTime(iso) {
  try {
    const d = new Date(iso);
    const s = (Date.now() - d.getTime()) / 1000;
    if (s < 60) return "agora";
    if (s < 3600) return `${Math.floor(s / 60)} min`;
    if (s < 86400) return `${Math.floor(s / 3600)} h`;
    return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const LIMIT = 50;

export default function ActivityLogPanel({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [q, setQ] = useState("");

  const load = async (skip = 0) => {
    setLoading(true);
    try {
      const r = await api.get("/activity-log", { params: { limit: LIMIT, skip } });
      const rows = r.data?.items || [];
      setItems((prev) => (skip === 0 ? rows : [...prev, ...rows]));
      setHasMore(rows.length === LIMIT);
    } catch { /* 403 or network — leave empty */ }
    setLoading(false);
  };

  useEffect(() => { load(0); }, []);

  const filtered = items.filter((it) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    const actionLabel = t(ACTION_META[it.action]?.key || "") || it.action || "";
    return [it.user_name, it.entity_label, it.details, it.store_name, actionLabel]
      .join(" ").toLowerCase().includes(s);
  });

  return (
    <div data-testid="activity-log-panel">
      <div className="mb-8">
        <p className="label-eyebrow text-primary mb-2">{t("activity_tab")}</p>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">{t("activity_title")}</h1>
        <p className="text-text-secondary text-sm mt-2">{t("activity_hint")}</p>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
        <input
          data-testid="activity-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("activity_search")}
          className="w-full bg-surface border border-border pl-9 pr-3 h-11 text-sm focus:border-primary focus:outline-none"
        />
      </div>

      <div className="border border-border">
        {loading && items.length === 0 ? (
          <div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-4 border-b border-border last:border-b-0">
                <div className="w-8 h-8 bg-surface animate-pulse shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-surface animate-pulse w-3/4" />
                  <div className="h-2.5 bg-surface animate-pulse w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="activity-empty" className="text-center py-14">
            <History size={36} className="mx-auto text-text-secondary/30 mb-3" />
            <p className="text-text-secondary text-sm">{t("activity_empty")}</p>
          </div>
        ) : (
          filtered.map((it) => {
            const m = ACTION_META[it.action] || { icon: History, cls: "text-text-secondary", key: "" };
            const Icon = m.icon;
            return (
              <div
                key={it.id}
                data-testid={`activity-row-${it.id}`}
                className="flex items-start gap-3 px-4 py-3 border-b border-border last:border-b-0 hover:bg-surface/50 transition-colors"
              >
                <div className={`mt-0.5 shrink-0 ${m.cls}`}><Icon size={16} /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm">
                    <span className="font-display font-bold">{it.user_name}</span>{" "}
                    <span className="text-text-secondary">{t(m.key) || it.action}</span>{" "}
                    {it.entity_label && <span className="font-display font-bold">{it.entity_label}</span>}
                  </p>
                  {it.details && <p className="text-xs text-text-secondary mt-0.5 truncate">{it.details}</p>}
                </div>
                <div className="shrink-0 text-right">
                  {it.store_name && (
                    <span className="block text-[9px] uppercase tracking-widest text-text-secondary border border-border px-1.5 py-0.5 mb-1">
                      {it.store_name}
                    </span>
                  )}
                  <span className="text-[10px] text-text-secondary whitespace-nowrap">{relTime(it.created_at)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {hasMore && !q.trim() && (
        <button
          data-testid="activity-load-more"
          onClick={() => load(items.length)}
          disabled={loading}
          className="mt-4 px-5 py-2.5 border border-border hover:border-primary hover:text-primary text-xs font-display font-bold uppercase tracking-widest transition-colors disabled:opacity-50"
        >
          {t("activity_load_more")}
        </button>
      )}
    </div>
  );
}
